var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import ImageGallery from '../ImageGallery/ImageGallery';
import { isDuplicateFile } from '../util';
import { addClassEl, removeClassEl } from '../util/dom';
import withRender from './ImageUploader.html';
// Default allowed size is 8MB
const DEFULT_ALLOWED_FILE_SIZE_BYTES = 8000000;
const DROP_ZONE_CLASS = 'm-panel IUC__drop-zone';
const DROP_ZONE_HOVER_CLASS = 'IUC__drop-zone--hover';
const UPLOAD_BUTTON_CLASS = 'm-panel IUC__upload-btn';
const MULTIPLE_IMAGES_ERROR = `You can't upload multiple images.`;
const FILE_SIZE_UNIT = 'MB';
const DEFAULT_OPTIONS = {
    showDropZone: true,
    allowedFileSize: DEFULT_ALLOWED_FILE_SIZE_BYTES,
    allowMultipleImages: true,
    allowedFileTypes: ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'],
    maxFilesLimit: 10,
    gridCellHeight: 200,
    gridCellWidth: 200,
    uploaderStyleClass: DROP_ZONE_CLASS,
    showErrors: true,
    showDefaultGallery: true,
    uploadButtonText: 'Select image/s',
    hideButtonAfterUpload: true,
    uploadDropZoneText: 'Select image/s or drag it here.',
    uploadedImages: [],
    useCustom: false
};
let ImageUploader = class ImageUploader extends Vue {
    constructor() {
        super(...arguments);
        this.validFiles = [];
        this.fileUrls = [];
        // Mandetory errors.
        this.error = '';
        // Errors related to invalid images.
        this.invalidImageErrors = [];
    }
    get uploaderOptions() {
        const uploaderOptions = Object.assign({}, DEFAULT_OPTIONS, this.options);
        return Object.assign({}, uploaderOptions, { allowedFileSize: DEFULT_ALLOWED_FILE_SIZE_BYTES, maxFilesLimit: uploaderOptions.allowMultipleImages
                ? uploaderOptions.maxFilesLimit
                : 1, uploaderStyleClass: uploaderOptions.showDropZone
                ? DROP_ZONE_CLASS
                : !uploaderOptions.useCustom ? UPLOAD_BUTTON_CLASS : '' });
    }
    /* TODO: Check upload multiple images conditions */
    get showDropZoneWrapper() {
        return this.uploaderOptions.uploadedImages.length === 0 && (this.uploaderOptions.maxFilesLimit === -1 ||
            (!this.uploaderOptions.allowMultipleImages && !this.uploaderOptions.hideButtonAfterUpload) ||
            this.validFiles.length < this.uploaderOptions.maxFilesLimit);
    }
    get imageGalleryOptions() {
        return {
            useAsImagePreview: !this.uploaderOptions.allowMultipleImages,
            images: this.uploaderOptions.uploadedImages.length > 0 ? this.uploaderOptions.uploadedImages : this.imageUrls,
            gridCellHeight: this.uploaderOptions.gridCellHeight,
            gridCellWidth: this.uploaderOptions.gridCellWidth
        };
    }
    get showErrors() {
        return this.invalidImageErrors.length > 0 && this.uploaderOptions.showErrors;
    }
    get imageUrls() {
        return this.validFiles
            ? this.validFiles.map((file) => URL.createObjectURL(file))
            : [];
    }
    onDrop($event) {
        this.invalidImageErrors = [];
        removeClassEl(this.$refs.dropZone, DROP_ZONE_HOVER_CLASS);
        // Added condition to make TypeScript happy
        if (!$event.dataTransfer) {
            return;
        }
        const files = Array.from($event.dataTransfer.files);
        if (this.isWithinLimit(files)) {
            this.selectValidFiles(files);
        }
        // Emit errors if any.
        if (this.invalidImageErrors.length > 0) {
            this.$emit('error', this.invalidImageErrors);
        }
        $event.stopPropagation();
        $event.preventDefault();
    }
    onDragEnter($event) {
        addClassEl(this.$refs.dropZone, DROP_ZONE_HOVER_CLASS);
        $event.stopPropagation();
        $event.preventDefault();
    }
    onDragLeave($event) {
        removeClassEl(this.$refs.dropZone, DROP_ZONE_HOVER_CLASS);
        $event.stopPropagation();
        $event.preventDefault();
    }
    onDragOver($event) {
        $event.stopPropagation();
        $event.preventDefault();
    }
    onFileChange() {
        this.invalidImageErrors = [];
        const imageFileInput = this.$refs.fileInput;
        const files = Array.from(imageFileInput.files);
        if (this.isWithinLimit(files)) {
            this.selectValidFiles(files);
        }
        // Emit errors if any.
        if (this.invalidImageErrors.length > 0) {
            this.$emit('error', this.invalidImageErrors);
        }
        // This is required to get rid of the tooltip of filelist shown by input element.
        imageFileInput.title = ' ';
    }
    onCloseImage(index) {
        this.invalidImageErrors = [];
        this.validFiles.splice(index, 1);
        this.$emit('change', this.validFiles);
    }
    /**
     * Verify all the files and eliminate the invalid files.
     * @param files - List of images to upload
     */
    isWithinLimit(files) {
        const totalFiles = files.length + this.validFiles.length;
        this.error = '';
        if (!this.options.allowMultipleImages && files.length > 1) {
            this.error = MULTIPLE_IMAGES_ERROR;
        }
        else if (this.uploaderOptions.showDefaultGallery && this.uploaderOptions.maxFilesLimit > -1 &&
            totalFiles > this.uploaderOptions.maxFilesLimit) {
            this.error = `You are allowed to upload only ${this.uploaderOptions.maxFilesLimit} images.`;
        }
        return this.error === '';
    }
    selectValidFiles(files) {
        files.forEach((file) => {
            // TODO: duplicate file should be checked only in case of drag/drop.
            if (this.isValidImage(file) && !isDuplicateFile(this.validFiles, file)) {
                if (this.uploaderOptions.allowMultipleImages) {
                    this.validFiles.push(file);
                }
                else {
                    this.validFiles = new Array(file);
                }
            }
        });
        // Emit only valid image files to be uploaded.
        this.$emit('change', this.validFiles);
    }
    /**
     * Validate file and check if it is of expected type and within file size limit.
     * @param file - Image file
     */
    isValidImage(file) {
        let isValid = true;
        const allowedFileTypes = this.uploaderOptions.allowedFileTypes;
        const allowedFileSize = (this.uploaderOptions.allowedFileSize / 1000000).toFixed(1) + FILE_SIZE_UNIT;
        if (file.size > this.uploaderOptions.allowedFileSize) {
            this.invalidImageErrors.push(`File '${file.name}' exceeds the allowed file size limit of ${allowedFileSize}`);
            isValid = false;
        }
        else if (allowedFileTypes.indexOf(file.type) < 0) {
            this.invalidImageErrors.push(`File '${file.name}' is not allowed.`);
            isValid = false;
        }
        return isValid;
    }
};
__decorate([
    Prop(),
    __metadata("design:type", Object)
], ImageUploader.prototype, "options", void 0);
__decorate([
    Prop(),
    __metadata("design:type", Boolean)
], ImageUploader.prototype, "isLoading", void 0);
ImageUploader = __decorate([
    withRender,
    Component({
        components: {
            'image-gallery': ImageGallery
        }
    })
], ImageUploader);
export default ImageUploader;
//# sourceMappingURL=ImageUploader.js.map